/* eslint-disable */
//@ts-nocheck
//TODO: fix types
import React, { useEffect, useMemo, useRef } from 'react';

import classes from './WheelPicker.module.css';

type WheelPickerProps = {
	hourItems: {
		label: string;
		value: string;
	}[];
	hourValue: string;
	minuteItems: {
		label: string;
		value: string;
	}[];
	minuteValue: string;
	containerHeight?: number;
	itemHeight?: number;
	onHourChange: (hour: number) => void;
	onMinuteChange: (minute: number) => void;
};

const WheelPickerComponent = ({
	hourItems,
	hourValue,
	onHourChange: handleHourChange,
	minuteItems,
	minuteValue,
	onMinuteChange: handleMinuteChange,
	containerHeight = 210,
	itemHeight = 32,
}: WheelPickerProps) => {
	const hourItemsContRef = useRef();
	const minuteItemsContRef = useRef();

	const isScrolling = useRef(false);
	const hourRefs = useRef([]);
	const minuteRefs = useRef([]);

	const hourItemsMap = useMemo(
		() => hourItems.reduce((map, item, index) => map.set(item.value, index), new Map()),
		[hourItems],
	);
	const minuteItemsMap = useMemo(
		() => minuteItems.reduce((map, item, index) => map.set(item.value, index), new Map()),
		[minuteItems],
	);
	const currentHourValue = useRef(hourItemsMap.get(hourValue) ?? 0);
	const currentMinuteValue = useRef(minuteItemsMap.get(minuteValue) ?? 0);

	const visibleItemsCount = Math.floor(containerHeight / itemHeight);
	const offset = Math.round((visibleItemsCount + 1) / 2) + 1;
	const maxScrollOffset = (containerHeight - itemHeight) / 1;

	function rerenderHourElements(
		selectedElement: number,
		scrollTop: number,
		firstItemIndex = Math.max(selectedElement - offset, 0),
		lastItemIndex = Math.min(selectedElement + offset, hourItems.length),
	) {
		if (hourRefs.current) {
			hourRefs.current.slice(firstItemIndex, lastItemIndex).forEach((item, index) => {
				const realIndex = index + firstItemIndex;
				const scrollOffset = Math.min(
					Math.abs(scrollTop - realIndex * itemHeight - itemHeight / 2),
					maxScrollOffset,
				);
				const sin = scrollOffset / maxScrollOffset;
				const cos = Math.sqrt(1 - sin ** 2);
				const [div] = item.getElementsByTagName('div');
				div.style.transform = `rotateX(${Math.asin(sin)}rad) scale(${cos})`;
				div.style.transformOrigin = 'center';
			});
		}
	}

	function rerenderMinuteElements(
		selectedElement: number,
		scrollTop: number,
		firstItemIndex = Math.max(selectedElement - offset, 0),
		lastItemIndex = Math.min(selectedElement + offset, minuteItems.length),
	) {
		if (minuteRefs.current) {
			minuteRefs.current.slice(firstItemIndex, lastItemIndex).forEach((item, index) => {
				const realIndex = index + firstItemIndex;
				const scrollOffset = Math.min(
					Math.abs(scrollTop - realIndex * itemHeight - itemHeight / 2),
					maxScrollOffset,
				);
				const sin = scrollOffset / maxScrollOffset;
				const cos = Math.sqrt(1 - sin ** 2);
				const [div] = item.getElementsByTagName('div');
				div.style.transform = `rotateX(${Math.asin(sin)}rad) scale(${cos})`;
				div.style.transformOrigin = 'left';
			});
		}
	}

	useEffect(() => {
		let isAnimating = false;

		function handleHourScroll(event: React.UIEvent<HTMLDivElement>) {
			if (!isAnimating) {
				isAnimating = true;

				requestAnimationFrame(() => {
					const scrollTop = Math.max(event.target.scrollTop, 0);
					const selectedElement = Math.min(
						Math.max(Math.floor(scrollTop / itemHeight), 0),
						hourItems.length - 1,
					);
					window.clearTimeout(isScrolling.current);
					rerenderHourElements(selectedElement, scrollTop);

					currentHourValue.current = selectedElement;
					isScrolling.current = setTimeout(function () {
						handleHourChange(hourItems[selectedElement].value);
					}, 200);

					isAnimating = false;
				});
			}
		}

		hourItemsContRef.current?.addEventListener('scroll', handleHourScroll);
		hourRefs.current[currentHourValue.current]?.scrollIntoView({
			block: 'center',
		});
		rerenderHourElements(
			currentHourValue.current,
			hourItemsContRef.current?.scrollTop,
			0,
			hourItems.length,
		);

		return () => {
			hourItemsContRef.current?.removeEventListener('scroll', handleHourScroll);
		};
	}, [hourItemsContRef.current, hourValue, minuteValue]);

	useEffect(() => {
		let isAnimating = false;

		function handleMinuteScroll(event) {
			if (!isAnimating) {
				isAnimating = true;

				requestAnimationFrame(() => {
					const scrollTop = Math.max(event.target.scrollTop, 0);
					const selectedElement = Math.min(
						Math.max(Math.floor(scrollTop / itemHeight), 0),
						minuteItems.length - 1,
					);
					window.clearTimeout(isScrolling.current);
					rerenderMinuteElements(selectedElement, scrollTop);

					currentMinuteValue.current = selectedElement;
					isScrolling.current = setTimeout(function () {
						handleMinuteChange(minuteItems[selectedElement].value);
					}, 200);

					isAnimating = false;
				});
			}
		}

		minuteItemsContRef.current?.addEventListener('scroll', handleMinuteScroll);
		minuteRefs.current[currentMinuteValue.current]?.scrollIntoView({
			block: 'center',
		});
		rerenderMinuteElements(
			currentMinuteValue.current,
			minuteItemsContRef.current?.scrollTop,
			0,
			minuteRefs.length,
		);
		return () => {
			minuteItemsContRef.current?.removeEventListener('scroll', handleMinuteScroll);
		};
	}, [minuteItemsContRef.current, minuteValue, hourValue]);

	useEffect(() => {
		const index = hourItemsMap.get(hourValue);
		if (index !== currentHourValue.current) {
			currentHourValue.current = index;
			hourRefs.current[index]?.scrollIntoView({
				block: 'center',
				behavior: 'smooth',
			});
		}
	}, [hourValue]);

	useEffect(() => {
		const index = minuteItemsMap.get(minuteValue);
		if (index !== currentMinuteValue.current) {
			currentMinuteValue.current = index;
			minuteRefs.current[index]?.scrollIntoView({
				block: 'center',
				behavior: 'smooth',
			});
		}
	}, [minuteValue]);

	return (
		<div
			className={classes.container}
			style={{
				height: `${containerHeight}px`,
			}}
		>
			<ul className={classes.items} ref={hourItemsContRef}>
				{hourItems.map((item, index) => (
					<li
						className={classes.item}
						key={item.value}
						ref={(node) => (hourRefs.current[index] = node)}
						style={{
							height: `${itemHeight}px`,
							lineHeight: `${itemHeight}px`,
						}}
						onClick={() => handleHourChange(Number(item.value))}
					>
						<div>{item.label}</div>
					</li>
				))}
			</ul>
			<div className={classes.separator}>:</div>
			<ul className={classes.items} ref={minuteItemsContRef}>
				{minuteItems.map((item, index) => (
					<li
						className={classes.item}
						key={item.value}
						ref={(node) => (minuteRefs.current[index] = node)}
						style={{
							height: `${itemHeight}px`,
							lineHeight: `${itemHeight}px`,
						}}
						onClick={() => handleMinuteChange(Number(item.value))}
					>
						<div>{item.label}</div>
					</li>
				))}
			</ul>
		</div>
	);
};

export const WheelPicker = WheelPickerComponent;
